<template>
  <button @click="exportPlanOrder" class="btn btn-sm btn-primary mr-1 mb-1">
    <i class="fa fa-download"></i>
    {{ $t('export') }}
    <b-modal v-model="exporting" :title="$t('exportingData')" @close="close" no-close-on-esc no-close-on-backdrop
      no-enforce-focus>
      <div class="alert alert-warning">
        <span>{{ $t('exportNotice') }}</span>
      </div>
      <b-progress :max="totalCount">
        <b-progress-bar :value="downloadedCount"
          :label="`${((downloadedCount / totalCount) * 100).toFixed(0)}%`"></b-progress-bar>
      </b-progress>
    </b-modal>
  </button>
</template>

<script>
import { saveAs } from 'file-saver';
import Excel from 'exceljs';
import moment from 'moment';

export default {
  props: ['filter', 'totalCount'],
  data() {
    return {
      exporting: false,
      cancelRequest: false,
      downloadedCount: 0
    }
  },
  methods: {
    exportPlanOrder: async function () {
      this.downloadedCount = 0;
      this.exporting = true;
      let workbook = new Excel.Workbook();
      let sheet = workbook.addWorksheet('data');
      this.setupSheet(sheet);

      let response = null;
      let params = Object.assign({}, this.filter);
      params.page = 1;
      params.pageSize = 1000;
      do {
        if (this.cancelRequest) {
          break;
        }
        let result = await this.$service.get('/plan-order/', {
          params: params
        });
        params.page += 1;
        response = result.data;
        response.docs.forEach(item => {
          let total = 0;
          if (item.type === 'new' || item.type === 'extend') {
            total = item.userPrice * item.userLimit * item.monthCount - item.discountAmount;
          }
          if (item.type === 'user') {
            total = item.userPrice * item.numberOfUserToBuy * item.monthCount - item.discountAmount;
          }
          if (item.type === 'upgrade') {
            total = item.userPriceDiff * item.userLimit * item.monthCount - item.discountAmount;
          }

          let source;
          if (!item.client.utm_source) {
            source = "Tự nhiên";
          } else if (item.client.utm_source && item.client.utm_source === 'sales') {
            source = "NVKD tự tìm"
          } else {
            source = "Marketing";
          }

          let status;
          if (!item.confirmTime && !item.cancelTime) {
            status = this.$t('waiting');
          } else if (item.confirmTime) {
            status = this.$t('confirmed');
          } else {
            status = this.$t('canceled');
          }

          // Số tháng tính từ đơn kích hoạt đầu tiên (activeTime) đến thời điểm xác nhận đơn (confirmTime).
          // Để trống nếu thiếu activeTime hoặc confirmTime.
          let monthsSinceActive = '';
          if ((item.activeTime || item.extendAgainTime) && item.confirmTime) {
            let months = moment(item.confirmTime).diff(moment(item.activeTime), 'months', true);
            if (item.isExtendAgain && item.extendAgainTime) {
              months = moment(item.confirmTime).diff(moment(item.extendAgainTime), 'months', true);
            }
            monthsSinceActive = months < 0 ? '' : Math.ceil(months);
          }

          let row = {
            clientName: item.client && item.client.companyName ? item.client.companyName : '',
            domain: item.client && item.client.domain ? item.client.domain : '',
            source: source,
            taxCode: item.client.taxCode ? item.client.taxCode : '',
            sales: item.sales ? item.sales.name : '',
            account: item.account ? item.account.name : '',
            type: this.$t(item.type),
            plan: item.plan && item.plan.name ? item.plan.name : this.$t('privatePlan'),
            upgradedPlan: item.upgradedPlan ? item.upgradedPlan.name : '',
            userPrice: item.userPrice ? item.userPrice : 0,
            userPriceDiff: item.userPriceDiff ? item.userPriceDiff : 0,
            userLimit: item.userLimit ? item.userLimit : 0,
            numberOfUserToBuy: item.numberOfUserToBuy ? item.numberOfUserToBuy : 0,
            monthCount: item.monthCount ? item.monthCount : 0,
            expireTime: moment(item.expireTime).format('DD/MM/YYYY HH:mm:ss'),
            discountAmount: item.discountAmount ? item.discountAmount : 0,
            total: total,
            status: status,
            createTime: moment(item.createTime).format('DD/MM/YYYY HH:mm:ss'),
            confirmTime: item.confirmTime ? moment(item.confirmTime).format('DD/MM/YYYY HH:mm:ss') : '',
            cancelTime: item.cancelTime ? moment(item.cancelTime).format('DD/MM/YYYY HH:mm:ss') : '',
            note: item.note ? item.note : '',
            activeTime: item.activeTime ? moment(item.activeTime).format('DD/MM/YYYY HH:mm:ss') : '',
            monthsSinceActive: monthsSinceActive,
          };
          sheet.addRow(row);
          this.downloadedCount += 1;
        });
      } while (response && response.hasNextPage);

      workbook.xlsx.writeBuffer({ base64: true }).then(data => {
        this.exporting = false;
        saveAs(new Blob([data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
          'plan-order.xlsx');
      }).finally(() => {
        this.cancelRequest = false;
        this.downloadedCount = 0;
      });
    },
    setupSheet: function (sheet) {
      sheet.columns = [
        { header: this.$t('clientName'), key: 'clientName', width: 15 },
        { header: this.$t('domain'), key: 'domain', width: 15 },
        { header: this.$t('source'), key: 'source', width: 15 },
        { header: this.$t('taxCode'), key: 'taxCode', width: 15 },
        { header: this.$t('sales'), key: 'sales', width: 15 },
        { header: this.$t('account'), key: 'account', width: 15 },
        { header: this.$t('type'), key: 'type', width: 15 },
        { header: this.$t('planName'), key: 'plan', width: 15 },
        { header: this.$t('upgradedPlanName'), key: 'upgradedPlan', width: 15 },
        { header: this.$t('userPrice'), key: 'userPrice', width: 15 },
        { header: this.$t('userPriceDiff'), key: 'userPriceDiff', width: 15 },
        { header: this.$t('userLimit'), key: 'userLimit', width: 15 },
        { header: this.$t('numberOfUserToBuy'), key: 'numberOfUserToBuy', width: 15 },
        { header: this.$t('monthCount'), key: 'monthCount', width: 15 },
        { header: this.$t('expireTime'), key: 'expireTime', width: 20 },
        { header: this.$t('discountAmount'), key: 'discountAmount', width: 15 },
        { header: this.$t('total'), key: 'total', width: 15 },
        { header: this.$t('status'), key: 'status', width: 15 },
        { header: this.$t('createTime'), key: 'createTime', width: 20 },
        { header: this.$t('confirmTime'), key: 'confirmTime', width: 20 },
        { header: this.$t('cancelTime'), key: 'cancelTime', width: 15 },
        { header: this.$t('note'), key: 'note', width: 15 },
        { header: this.$t('activeTime'), key: 'activeTime', width: 20 },
        { header: this.$t('monthsSinceActive'), key: 'monthsSinceActive', width: 15 }
      ];
    },
    close() {
      this.cancelRequest = true;
    }
  },
  i18n: {
    messages: {
      vi: {
        waiting: 'Đợi xác nhận',
        confirmed: 'Đã xác nhận',
        canceled: 'Đã huỷ',
        client: 'Khách hàng',
        taxCode: 'Mã số thuế',
        type: 'Loại',
        planName: 'Tên gói',
        upgradedPlanName: 'Tên gói sau nâng cấp',
        userPrice: 'Giá/user',
        userPriceDiff: 'Giá chênh lệch/ user',
        userLimit: 'Số user tối đa',
        numberOfUserToBuy: 'Số user mua thêm',
        monthCount: 'Số tháng',
        expireTime: 'Ngày hết hạn',
        discountAmount: 'Giảm giá',
        total: 'Tổng',
        createTime: 'Thời gian tạo',
        confirmTime: 'Thời gian xác nhận',
        cancelTime: 'Thời gian huỷ',
        note: 'Ghi chú',
        privatePlan: 'Gói đặc biệt',
        exportingData: 'Đang xuất dữ liệu',
        exportNotice: 'Dữ liệu đang được tải xuống, vui lòng đợi xong giây lát...',
        new: 'Mới',
        upgrade: 'Nâng cấp',
        extend: 'Gia hạn',
        user: 'Thêm tài khoản người dùng',
        setup: 'Cài đặt',
        newDivide: 'Mới - do tách domain',
        unlimited: 'Không giới hạn (Flex)',
        promo: 'Khuyến mại',
        clientName: 'Tên khách hàng / công ty',
        sales: 'Sales',
        account: 'Chăm sóc',
        source: "Nguồn",
        companySource: "Nguồn công ty",
        selfFindingSource: "Nguồn tự tìm",
        activeTime: "Thời gian kích hoạt",
        monthsSinceActive: "Số tháng kể từ kích hoạt",
      },
      en: {
        waiting: 'Đợi xác nhận',
        confirmed: 'Đã xác nhận',
        canceled: 'Đã huỷ',
        client: 'Client',
        taxCode: 'Tax code',
        type: 'Type',
        planName: 'Plan name',
        upgradedPlanName: 'Upgraded plan name',
        userPrice: 'Price/user',
        userPriceDiff: 'Price difference/ user',
        userLimit: 'User limit',
        numberOfUserToBuy: 'Number of user to buy more',
        monthCount: 'Number of month',
        expireTime: 'Expire time',
        discountAmount: 'Discount amount',
        total: 'Total',
        createTime: 'Create time',
        confirmTime: 'Confirm time',
        cancelTime: 'Cancel time',
        note: 'Note',
        privatePlan: 'Private plan',
        exportingData: 'Exporting data',
        exportNotice: 'Data is being exported. Please wait for a few moment...',
        new: 'New',
        upgrade: 'Upgrade',
        extend: 'Extend',
        user: 'Additional user',
        setup: 'Setup',
        newDivide: 'New - due to dividing domain',
        unlimited: 'Unlimited (Flex)',
        promo: 'Promotion',
        clientName: 'Client name / company name',
        sales: 'Sales',
        source: "Source",
        account: 'Account',
        companySource: "Company source",
        selfFindingSource: "Self finding source",
        activeTime: "Activation time",
        monthsSinceActive: "Months since activation",
      },
    }
  }
}
</script>
